import React from 'react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ShoppingBag, Globe, UtensilsCrossed, Pill, Package } from 'lucide-react';
import { Badge } from './ui/badge';

export const AppFeatures = () => {
  const features = [
    {
      id: 'local',
      label: 'Achat Local',
      icon: ShoppingBag,
      title: 'Commerce Local Simplifié',
      description: 'Accédez à tous les magasins et produits disponibles au Bénin directement depuis votre mobile.',
      benefits: [
        'Accès direct aux commerces béninois',
        'Paiement sécurisé et rapide',
        'Support aux producteurs locaux',
        'Produits authentiques garantis',
        'Expérience d\'achat unifiée',
      ],
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&auto=format&fit=crop',
    },
    {
      id: 'international',
      label: 'International',
      icon: Globe,
      title: 'Connexion Internationale',
      description: 'Commandez sur les grandes plateformes mondiales avec une logistique fiable et intégrée.',
      benefits: [
        'Amazon, Cdiscount, Shein, Alibaba...',
        'Processus logistique fiable',
        'Paiement sécurisé',
        'Livraison internationale trackée',
        'Service client dédié',
      ],
      image: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=800&auto=format&fit=crop',
    },
    {
      id: 'food',
      label: 'Livraison Repas',
      icon: UtensilsCrossed,
      title: 'Livraison de Repas',
      description: 'Soutenez le secteur de la restauration avec une traçabilité complète de la cuisine au client.',
      benefits: [
        'Traçabilité complète des commandes',
        'Code QR pour sécurité maximale',
        'Preuve de livraison',
        'Large choix de restaurants',
        'Temps de livraison optimisé',
      ],
      image: 'https://images.unsplash.com/photo-1526367790999-0150786686a2?w=800&auto=format&fit=crop',
    },
    {
      id: 'pharmacy',
      label: 'Pharmacie Drive',
      icon: Pill,
      title: 'Pharmacie Drive',
      description: 'Innovation dans l\'accès aux médicaments avec digitalisation et optimisation de la chaîne pharmaceutique.',
      benefits: [
        'Ordonnance numérisée sécurisée',
        'Distribution intelligente',
        'Confidentialité médicale garantie',
        'Livraison à domicile ou travail',
        'Service d\'urgence disponible',
      ],
      image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=800&auto=format&fit=crop',
    },
    {
      id: 'drive',
      label: 'Syne Drive',
      icon: Package,
      title: 'Casiers Sécurisés',
      description: 'Réseau de casiers physiques pour retrait sécurisé et flexible de vos colis 24/7.',
      benefits: [
        'Retrait 24/7 à votre convenance',
        'Sécurité maximale des colis',
        'Réseau de points relais',
        'Local et international',
        'Notifications en temps réel',
      ],
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop',
    },
  ];

  return (
    <section id="app" className="py-20 bg-background">
      <div className="container">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold text-primary mb-3 uppercase tracking-wider">Application SYNE</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Une App <span className="text-primary">Tout-en-Un</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            SYNE place le marché mondial à portée de main avec une interface intuitive et puissante
          </p>
        </div>

        <Tabs defaultValue="local" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto gap-2 bg-muted/50 p-2 rounded-xl mb-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <TabsTrigger
                  key={feature.id}
                  value={feature.id}
                  className="flex flex-col sm:flex-row items-center gap-2 py-3 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg transition-all"
                >
                  <Icon size={20} />
                  <span className="text-xs sm:text-sm font-medium">{feature.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <TabsContent key={feature.id} value={feature.id} className="mt-0">
                <Card className="border-border overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2 gap-0">
                      {/* Image */}
                      <div className="relative h-64 lg:h-auto order-2 lg:order-1">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-secondary/80 to-transparent"></div>
                      </div>

                      {/* Content */}
                      <div className="p-8 lg:p-12 order-1 lg:order-2">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                            <Icon className="text-primary" size={24} />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            Fonctionnalité SYNE
                          </Badge>
                        </div>

                        <h3 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-4">
                          {feature.title}
                        </h3>
                        <p className="text-muted-foreground mb-6 leading-relaxed">
                          {feature.description}
                        </p>

                        <div className="space-y-3">
                          <h4 className="font-semibold text-foreground text-sm uppercase tracking-wider mb-4">
                            Avantages Clés
                          </h4>
                          {feature.benefits.map((benefit, index) => (
                            <div key={index} className="flex items-start gap-3 group">
                              <div className="mt-1 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                <div className="w-2 h-2 rounded-full bg-primary"></div>
                              </div>
                              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                                {benefit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};